"use client";

import { useState } from "react";
import { CalendarDays } from "lucide-react";
import { costRangeParams, customRangeDays, MAX_CUSTOM_DAYS, type CostPeriod, type CostRange } from "@/lib/llm-models/cost-filters";
import { focusRing } from "./CostFilterControls";
import { plural } from "./format";

export const PERIODS: { id: CostPeriod; label: string; phrase: string }[] = [
  { id: "24h", label: "24 hours", phrase: "the last 24 hours" },
  { id: "7d", label: "7 days", phrase: "the last 7 days" },
  { id: "30d", label: "30 days", phrase: "the last 30 days" },
  { id: "90d", label: "90 days", phrase: "the last 90 days" },
];

/** Today's date in Pakistan, as "2026-10-22". */
function pakistanToday(): string {
  return new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** A date `days` before a "2026-10-22" date. */
function daysBefore(date: string, days: number): string {
  return new Date(new Date(`${date}T00:00:00Z`).getTime() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** "Oct 1, 2026" for a "2026-10-01" date. */
export function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { dateStyle: "medium", timeZone: "UTC" });
}

/** The range in a sentence: "in the last 30 days", "on Oct 1, 2026", "between Oct 1, 2026 and Oct 15, 2026". */
export function rangePhrase(range: CostRange): string {
  if ("period" in range) return `in ${PERIODS.find((p) => p.id === range.period)?.phrase ?? "this period"}`;
  return range.from === range.to ? `on ${dayLabel(range.from)}` : `between ${dayLabel(range.from)} and ${dayLabel(range.to)}`;
}

/** The range as a caption: "The last 30 days", "Oct 1, 2026", "Oct 1, 2026 – Oct 15, 2026". */
export function rangeLabel(range: CostRange): string {
  if ("period" in range) return (PERIODS.find((p) => p.id === range.period)?.phrase ?? "this period").replace(/^\w/, (c) => c.toUpperCase());
  return range.from === range.to ? dayLabel(range.from) : `${dayLabel(range.from)} – ${dayLabel(range.to)}`;
}

/** Adds the range's query parameters to `params`. */
export function withRange(params: URLSearchParams, range: CostRange): URLSearchParams {
  for (const [key, value] of costRangeParams(range)) params.set(key, value);
  return params;
}

const dateInputClass = `rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm ${focusRing}`;

/** The preset periods, plus any span of whole days in Pakistan time. */
export function RangePicker({ range, onChange }: { range: CostRange; onChange: (range: CostRange) => void }) {
  const [draft, setDraft] = useState<{ from: string; to: string } | null>(null);
  const custom = "from" in range ? range : null;
  const today = pakistanToday();

  const days = draft ? customRangeDays(draft.from, draft.to) : null;
  const problem = !draft
    ? null
    : !draft.from || !draft.to
      ? "Pick both dates."
      : days === null
        ? "Pick valid dates."
        : days < 1
          ? "The start date is after the end date."
          : days > MAX_CUSTOM_DAYS
            ? `Pick at most ${MAX_CUSTOM_DAYS} days.`
            : draft.from > today
              ? "The start date is in the future."
              : null;

  const pill = (active: boolean) =>
    `rounded-md px-2.5 py-1.5 text-sm font-medium transition sm:px-3 ${focusRing} ${
      active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Period" className="inline-flex flex-wrap rounded-lg bg-slate-100 p-1">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={!draft && "period" in range && range.period === p.id}
            onClick={() => {
              setDraft(null);
              onChange({ period: p.id });
            }}
            className={pill(!draft && "period" in range && range.period === p.id)}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={!!draft || !!custom}
          aria-expanded={!!draft}
          onClick={() => setDraft(draft ? null : custom ? { from: custom.from, to: custom.to } : { from: daysBefore(today, 6), to: today })}
          className={`inline-flex items-center gap-1.5 ${pill(!!draft || !!custom)}`}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          {custom ? rangeLabel(custom) : "Custom"}
        </button>
      </div>
      {draft ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (problem) return;
            setDraft(null);
            onChange({ from: draft.from, to: draft.to });
          }}
        >
          <label className="text-xs font-medium text-slate-500">
            From
            <input
              type="date"
              value={draft.from}
              max={today}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              className={`mt-1 block ${dateInputClass}`}
            />
          </label>
          <label className="text-xs font-medium text-slate-500">
            To
            <input
              type="date"
              value={draft.to}
              max={today}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              className={`mt-1 block ${dateInputClass}`}
            />
          </label>
          <button
            type="submit"
            disabled={!!problem}
            className={`rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`}
          >
            Apply
          </button>
          <button type="button" onClick={() => setDraft(null)} className={`rounded px-1 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 ${focusRing}`}>
            Cancel
          </button>
          <p className={`basis-full text-xs ${problem ? "text-red-600" : "text-slate-500"}`}>
            {problem ?? `${plural(days ?? 0, "day")}, in Pakistan time. Both dates are included.`}
          </p>
        </form>
      ) : null}
    </div>
  );
}
